import React,{useState, useEffect, useMemo} from "react";
import Header from "../components/headerPage";
import Footer from "../components/footer";
import image from "../../assets/images/convocatoria.jpg";
import {db} from '../../config/firebase';
import dbRef from '../../config/dbRef';
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { OnLoad } from "../components/onload";
import { FaSearch, FaLayerGroup, FaMapMarkerAlt, FaRegCalendarAlt } from "react-icons/fa";

const AREA_LABELS = {
  juridico: 'Jurídico',
  medico: 'Medicina',
  academico: 'Académico',
  policial: 'Policial',
  teacher: 'ESFM',
};

const STATUS_META = {
  activo: { bar: 'bg-primary', badge: 'bg-primary bg-opacity-10 text-primary', label: 'Vigente' },
  proximo: { bar: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700', label: 'Próximamente' },
  pasado: { bar: 'bg-gray-300', badge: 'bg-gray-100 text-gray-500', label: 'Pasada' },
};

const parseFecha = (fecha) => {
  if (!fecha) return null;
  const parts = String(fecha).split('/');
  if (parts.length === 3) {
    return new Date(`${parts[1]}/${parts[0]}/${parts[2]}`);
  }
  const parsed = new Date(fecha);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getEstado = (item) => {
  if (item.data && item.data.estado) return item.data.estado;
  const fecha = parseFecha(item.data && item.data.fecha);
  if (!fecha) return 'activo';
  return fecha >= new Date() ? 'activo' : 'pasado';
};

const ConvocatoriaCard = ({ item }) => {
  const data = item.data || {};
  const estado = getEstado(item);
  const meta = STATUS_META[estado] || STATUS_META.activo;
  const areaLabel = AREA_LABELS[data.area] || data.area;

  return (
    <div className="bg-white rounded-2xl h-full border border-gray-200 shadow-sm hover:shadow-lg hover:border-primary transition duration-300 overflow-hidden flex flex-col">
      <div className={`h-1.5 w-full ${meta.bar}`}></div>
      <div className="px-6 py-5 flex flex-col justify-between flex-1">
        <div>
          <span className={`inline-block text-xs font-semibold uppercase tracking-wide rounded-full px-3 py-1 mb-3 ${meta.badge}`}>
            {meta.label}
          </span>
          <h3 className="font-bold text-lg text-fondo leading-snug">{data.nombre}</h3>
          {data.descripcion && (
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">{data.descripcion}</p>
          )}
        </div>
        <div className="flex flex-row flex-wrap mt-5 text-sm text-gray-600">
          {data.fecha && (
            <div className="flex items-center mr-4 mb-2">
              <FaRegCalendarAlt className="text-primary mr-2"/>
              <span>{data.fecha}</span>
            </div>
          )}
          <div className="flex items-center mr-4 mb-2">
            <FaLayerGroup className="text-primary mr-2"/>
            <span>{data.materias ? data.materias.length : 0} áreas</span>
          </div>
          {areaLabel && (
            <div className="flex items-center mb-2">
              <FaMapMarkerAlt className="text-primary mr-2"/>
              <span>{areaLabel}</span>
            </div>
          )}
        </div>
        <div className="mt-2">
          {estado === 'activo' ? (
            <Link
              className="inline-block text-white font-semibold py-2 px-5 rounded-full hover:bg-fondo border-2 border-primary bg-primary"
              to={{ pathname: '/Details', search: `?idExam=${item.id}`, data: item }}
            >
              Ver detalles
            </Link>
          ) : estado === 'proximo' ? (
            <p className="text-fondo font-medium py-2 px-4 rounded-full border border-gray-200 bg-gray-50 inline-block">
              {data.fecha || 'Próximamente'}
            </p>
          ) : (
            <Link
              className="inline-block text-primary font-semibold py-2 px-5 rounded-full border-2 border-primary hover:bg-primary hover:text-white"
              to="/contact"
            >
              Contáctanos
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 6;

const Details = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('todas');
  const [areaFilter, setAreaFilter] = useState('todas');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const bar = document.getElementById('bar');
    if (bar) bar.style.display = 'block';
  }, []);

  useEffect(() => {
    getDocs(collection(db, dbRef.exam))
      .then((response) => {
        const exams = [];
        response.forEach((doc) => {
          const data = doc.data();
          if (data.visible === false) return;
          exams.push({ id: doc.id, data });
        });
        setItems(exams);
      })
      .finally(() => setLoading(false));
  }, []);

  const areas = useMemo(() => {
    const unique = Array.from(new Set(items.map((item) => item.data.area).filter(Boolean)));
    return unique;
  }, [items]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((item) => {
      const data = item.data || {};
      const estado = getEstado(item);
      const haystack = [data.nombre, data.descripcion, data.area, AREA_LABELS[data.area], data.fecha]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesQuery = !term || haystack.indexOf(term) !== -1;
      const matchesStatus = statusFilter === 'todas' || estado === statusFilter;
      const matchesArea = areaFilter === 'todas' || data.area === areaFilter;
      return matchesQuery && matchesStatus && matchesArea;
    });
  }, [items, query, statusFilter, areaFilter]);

  useEffect(() => {
    setPage(0);
  }, [query, statusFilter, areaFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const statusChips = [
    { id: 'todas', label: 'Todas' },
    { id: 'activo', label: 'Vigentes' },
    { id: 'proximo', label: 'Próximas' },
    { id: 'pasado', label: 'Pasadas' },
  ];

  return (
    <>
      <Header titulo={"Convocatorias"} nombre={"Convocatoria"} mensaje={"Tu futuro aquí"} image={image} posicion={'center'} />

      <div className="w-full bg-gray-100 py-12 px-6 md:px-12">
        <div className="landing-wrap">
          <p className="text-primary text-sm font-bold uppercase tracking-widest text-center">Convocatorias</p>
          <h1 className="text-3xl md:text-4xl font-bold text-fondo text-center mt-2">Encuentra tu convocatoria</h1>
          <p className="mt-3 text-center text-gray-600 max-w-2xl mx-auto">
            Te presentamos las convocatorias que hemos atendido. Si no encuentras la tuya, escríbenos y te ayudamos.
          </p>

          <div className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm px-5 py-3 flex items-center">
            <FaSearch className="text-gray-400 mr-3" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nombre, área o fecha..."
              className="flex-1 bg-transparent outline-none text-fondo placeholder-gray-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-sm text-primary font-semibold ml-3"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="flex flex-row flex-wrap justify-center mt-5">
            {statusChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => setStatusFilter(chip.id)}
                className={`text-sm font-semibold rounded-full px-4 py-2 m-1 border ${statusFilter === chip.id ? 'bg-primary text-white border-primary' : 'bg-white text-fondo border-gray-200 hover:border-primary'}`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {areas.length > 1 && (
            <div className="flex flex-row flex-wrap justify-center mt-2">
              <button
                type="button"
                onClick={() => setAreaFilter('todas')}
                className={`text-sm rounded-full px-4 py-2 m-1 border ${areaFilter === 'todas' ? 'bg-fondo text-white border-fondo' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                Todas las áreas
              </button>
              {areas.map((area) => (
                <button
                  key={area}
                  type="button"
                  onClick={() => setAreaFilter(area)}
                  className={`text-sm rounded-full px-4 py-2 m-1 border ${areaFilter === area ? 'bg-fondo text-white border-fondo' : 'bg-white text-gray-600 border-gray-200'}`}
                >
                  {AREA_LABELS[area] || area}
                </button>
              ))}
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-4">
            {loading ? 'Cargando convocatorias...' : `${filtered.length} convocatoria${filtered.length === 1 ? '' : 's'}`}
          </p>

          {loading ? (
            <div className="flex flex-col items-center py-16">
              <OnLoad color="#e84529"/>
            </div>
          ) : pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {pageItems.map((item) => (
                  <ConvocatoriaCard key={item.id} item={item} />
                ))}
              </div>
              {filtered.length > PAGE_SIZE && (
                <div className="flex flex-row items-center justify-center mt-10">
                  <button
                    type="button"
                    onClick={() => setPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className={`py-2 px-5 rounded-l-full font-semibold ${currentPage === 0 ? 'bg-gray-200 text-gray-400' : 'bg-white text-fondo border border-gray-200 hover:border-primary'}`}
                  >
                    Anterior
                  </button>
                  <span className="py-2 px-5 bg-white border-t border-b border-gray-200 text-sm text-gray-600">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className={`py-2 px-5 rounded-r-full font-semibold ${currentPage >= totalPages - 1 ? 'bg-gray-200 text-gray-400' : 'bg-white text-fondo border border-gray-200 hover:border-primary'}`}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 text-center py-12 px-6 mt-8">
              <p className="font-bold text-fondo text-xl">No encontramos convocatorias</p>
              <p className="text-gray-600 mt-2">Prueba con otro término o avísanos si conoces una que no esté aquí.</p>
              <Link className="inline-block bg-primary text-white font-semibold mx-auto py-2 px-5 rounded-full mt-5 hover:bg-fondo" to="/contact">
                Contáctanos
              </Link>
              <p className="text-gray-500 mt-4 text-sm">Si nos avisas de una convocatoria nueva, puedes ganar una suscripción.</p>
            </div>
          )}
        </div>
      </div>
      <Footer/>
    </>
  );
}

export default Details;
